import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  FormHelperText,
  TextField
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import Label from "./../../../components/Label/index";
import appcolors from "../../../constants/Colors";
import globalStyle from "../../../globalStyle";
import { TimePicker } from "@mui/x-date-pickers";
import { allIcons } from "../../../assets";
import GlobalButton from "../../../components/CustomButton";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import dayjs from 'dayjs';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing("24px"),
  },
  "& .MuiPaper-root": {
    width: "600px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

let paramsData = {};

const AddEditShiftDialog = forwardRef(({}, ref) => {
  const [open, setOpen] = useState(false);
  const [shifts, setShifts] = useState([
    {
      startTime: dayjs().hour(10).minute(0).format('h:mm A'),  // Default to 10 AM
      endTime: dayjs().hour(19).minute(0).format('h:mm A'),    // Default to 7 PM
      type: "shift",
    },
  ]);
  const [error, setError] = useState({});
  
  useImperativeHandle(ref, () => ({
    isVisible(params) {
      paramsData = params;
      setOpen(true);
    },
  }));

  useEffect(() => {
    if (open) {
      // Reset shifts and errors when dialog opens
      setShifts([
        {
          startTime: dayjs().hour(10).minute(0).format('h:mm A'),
          endTime: dayjs().hour(19).minute(0).format('h:mm A'),
          type: "shift",
        },
      ]);
      setError({});
    }
  }, [open]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleAddShift = () => {
    if (validateShifts()) {
      // Get the end time of the last shift and add 1 hour to set the new shift's start time
      const lastShiftEndTime = dayjs(shifts[shifts.length - 1].endTime, 'h:mm A');
      const newStartTime = lastShiftEndTime.add(1, 'hour');
      const newEndTime = newStartTime.add(1, 'hour');

      setShifts([
        ...shifts,
        {
          startTime: newStartTime.format('h:mm A'),
          endTime: newEndTime.format('h:mm A'),
          type: "shift",
        },
      ]);
      setError({});
    } else {
      setError({ general: "Please fix the overlapping shifts." });
    }
  };

  const handleDeleteShift = (ind) => {
    if (shifts.length > 1) {
      let tempArray = [...shifts];
      tempArray.splice(ind, 1);
      setShifts(tempArray);
    }
  };

  const handleShiftChange = (ind, value, name) => {
    let tempArray = [...shifts];
    tempArray[ind][name] = value.format('h:mm A');  // Ensure time format is h:mm A
    setShifts(tempArray);
    validateShifts();
  };

  const validateShifts = () => {
    let errors = {};
    let hasError = false;

    for (let i = 0; i < shifts.length; i++) {
      const start1 = dayjs(shifts[i].startTime, 'h:mm A');
      const end1 = dayjs(shifts[i].endTime, 'h:mm A');
      
      if (start1.isSameOrAfter(end1)) {
        errors[`shift${i}`] = "Start time should be before end time";
        hasError = true;
      } else if (shifts.slice(0, i).some((shift) => {
        const start2 = dayjs(shift.startTime, 'h:mm A');
        const end2 = dayjs(shift.endTime, 'h:mm A');
        return (start1.isBefore(end2) && start2.isBefore(end1));
      })) {
        errors[`shift${i}`] = "This time overlaps with another shift";
        hasError = true;
      }
    }

    setError(errors);
    return !hasError;
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <IconButton
        sx={{ position: "absolute", top: "5px", right: "5px" }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle
        variant="h1"
        fontSize={"1.325rem"}
        fontWeight={600}
        id="customized-dialog-title"
      >
        Sinan's shift Mon, 8 Jul
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} alignItems={"flex-end"}>
          <Grid item xs={5}>
            <Label
              labelText="Start time"
              labelProps={{ color: appcolors.black, fontWeight: 600 }}
            />
          </Grid>
          <Grid item xs={5}>
            <Label
              labelText="End time"
              labelProps={{ color: appcolors.black, fontWeight: 600 }}
            />
          </Grid>
          <Grid item xs={2}></Grid>
        </Grid>
        <Grid container spacing={2} alignItems={"flex-end"}>
          {shifts.map((val, _ind) => (
            <React.Fragment key={_ind}>
              <Grid item xs={5}>
                <TimePicker
                  sx={globalStyle.textField}
                  value={dayjs(val.startTime, 'h:mm A')}  // Convert the string to a Day.js object
                  onChange={(newValue) =>
                    handleShiftChange(_ind, newValue, "startTime")
                  }
                  ampm={true}  // Enable AM/PM format
                />
                {error[`shift${_ind}`] && (
                  <FormHelperText error>{error[`shift${_ind}`]}</FormHelperText>
                )}
              </Grid>
              <Grid item xs={5}>
                <TimePicker
                  sx={globalStyle.textField}
                  value={dayjs(val.endTime, 'h:mm A')}  // Convert the string to a Day.js object
                  name="endTime"
                  onChange={(newValue) =>
                    handleShiftChange(_ind, newValue, "endTime")
                  }
                  ampm={true}  // Enable AM/PM format
                />
                {error[`shift${_ind}`] && (
                  <FormHelperText error>{error[`shift${_ind}`]}</FormHelperText>
                )}
              </Grid>
              <Grid item xs={2} alignSelf={"flex-end"}>
                <IconButton
                  onClick={() => {
                    handleDeleteShift(_ind);
                  }}
                >
                  <img src={allIcons.deleteIcon} width={25} />
                </IconButton>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
        <GlobalButton
          disableRipple={true}
          btnText={"Add a shift"}
          startIcon={<AddCircleOutlineIcon />}
          handleClickBtn={handleAddShift}
          styleProps={{
            marginTop: "15px",
            backgroundColor: "transparent",
            color: "#000000",
            padding: "0px",
            width: "auto",
            textTransform: "capitalize",
            fontWeight: 500,
            border: "none",
            borderRadius: "0px",
          }}
          hoverProperties={{ backgroundColor: "transparent" }}
        />
        {error.general && (
          <FormHelperText error>{error.general}</FormHelperText>
        )}
      </DialogContent>
      <DialogActions>
        <GlobalButton
          variant={"outlined"}
          btnText={"Cancel"}
          styleProps={{ width: 100 }}
          handleClickBtn={handleClose}
        />
        <GlobalButton
          variant={"contained"}
          btnText={"Save"}
          styleProps={{ width: 90 }}
          handleClickBtn={() => {
            if (validateShifts()) {
              // Save shifts logic
              handleClose();
            } else {
              setError({ general: "Please fix the overlapping shifts." });
            }
          }}
        />
      </DialogActions>
    </BootstrapDialog>
  );
});

export default AddEditShiftDialog;
